import { Container, Sprite, type Text, type Texture } from "pixi.js";
import type { PresentationTaskRunner, ViewportStore } from "@slot/presentation";
import { LocalizedText, ModalChrome, bindTap } from "@slot/game-shell";
import type { FeatureTriggerContent, FeatureTriggerPort, LocalizationService } from "@slot/game-shell";
import { POTS, type PotKind } from "../../rules/symbols";
import { POT_COLOURS } from "../../scene/PotsView";
import { PanelMotion, type PanelMotionTuning } from "../PanelMotion";
import { FONT, TEXT, label, value } from "../textStyles";

/** The card (the art's 900 × 640) with the title, the respins and the boosters the bonus starts with. */
const GEOMETRY = {
  content: { width: 1000, height: 760, y: { desktop: 0, mobile: -200, mobile_landscape: 0 } },
  card: { width: 900, height: 640 },
  title: { y: -205, fontSize: 92, box: { width: 760, height: 120 } },
  amount: { y: -48, fontSize: 170 },
  subtitle: { y: 70, fontSize: 64, box: { width: 700, height: 90 } },
  boosters: { y: 186, fontSize: 52, gap: 210, box: { width: 200, height: 70 } },
  none: { y: 186, fontSize: 40, box: { width: 700, height: 60 } },
} as const;

const BOOSTER_NAMES: Readonly<Record<PotKind, string>> = { life: "LIFE", multi: "MULTI", grow: "GROW" };

/** The way into the bonus the shell plays as the card is dismissed (`BonusTransition`, the game transition's). */
interface BonusEntry {
  readonly playing: boolean;
  whenCovered(): Promise<void>;
}

/**
 * The bonus's start behind `FeatureTriggerPort`: "HOLD & WIN / 3 / RESPINS" on the red card, and the boosters the
 * charged pots bring (or a line saying none do). The boosters are this title's: the pots feature sets them before the
 * shell shows the panel (`setBoosters`). Dismissed, it goes under the way into the bonus: the shell starts the artist's
 * potion explosion (the game transition), the card shrinks out with its dim kept, and it is gone once the explosion
 * covers the screen, so the round goes into the bonus under it.
 */
export class FeatureTriggerView extends Container implements FeatureTriggerPort {
  private readonly chrome: ModalChrome;
  private readonly motion: PanelMotion;
  private readonly amountLabel: Text;
  private readonly boosterLabels: Record<PotKind, LocalizedText>;
  private readonly noBoosters: LocalizedText;
  private readonly entry: BonusEntry;

  public constructor(input: { card: Texture | null; viewport: ViewportStore; localization: LocalizationService; runner: PresentationTaskRunner; motion: PanelMotionTuning; entry: BonusEntry }) {
    super({ label: "FeatureTriggerPanel" });
    const g = GEOMETRY;
    this.chrome = new ModalChrome({
      label: "root",
      viewport: input.viewport,
      localization: input.localization,
      runner: input.runner,
      content: { ...g.content, limits: { desktop: { maxHeight: 1300 }, mobile: { maxWidth: 1040 }, mobile_landscape: {} } },
    });
    this.addChild(this.chrome);

    const panel = new Container({ label: "Panel" });
    if (input.card) {
      const card = new Sprite({ texture: input.card, anchor: 0.5, label: "Card" });
      card.setSize(g.card.width, g.card.height);
      panel.addChild(card);
    }
    this.amountLabel = value({ style: TEXT.amount, fontSize: g.amount.fontSize, y: g.amount.y, label: "AmountLabel" });
    panel.addChild(
      label({ localization: input.localization, key: "featuretrigger.title", fallback: "HOLD & WIN", style: TEXT.heading(), ...g.title, label: "Title" }),
      this.amountLabel,
      label({ localization: input.localization, key: "featuretrigger.subtitle", fallback: "RESPINS", style: TEXT.heading(), ...g.subtitle, label: "SubTitle" }),
    );
    const boosters = POTS.map((kind, i) => {
      const colours = POT_COLOURS[kind];
      const text = new LocalizedText({
        localization: input.localization,
        key: `pots.${kind}`,
        fallback: BOOSTER_NAMES[kind],
        style: { fontFamily: FONT, fontWeight: "900", fontSize: g.boosters.fontSize, fill: colours.fill, stroke: { color: colours.stroke, width: 10, join: "round" }, align: "center" },
        label: `Booster:${kind}`,
        fitBox: g.boosters.box,
      });
      text.position.set((i - 1) * g.boosters.gap, g.boosters.y);
      text.visible = false;
      panel.addChild(text);
      return [kind, text] as const;
    });
    this.boosterLabels = Object.fromEntries(boosters) as Record<PotKind, LocalizedText>;
    this.noBoosters = label({ localization: input.localization, key: "featuretrigger.noBoosters", fallback: "Fill the pots to start with boosters", style: TEXT.body, ...g.none, label: "NoBoosters" });
    panel.addChild(this.noBoosters);
    this.chrome.content.addChild(panel);
    this.motion = new PanelMotion({ chrome: this.chrome, panel, runner: input.runner, tuning: input.motion });
    this.entry = input.entry;
  }

  /** Whether the card is on screen (from its show to its hide). */
  public get shown(): boolean {
    return this.chrome.visible;
  }

  /** The boosters this bonus starts with (this title's addition to the port: the pots feature calls it). */
  public setBoosters(boosters: Readonly<Record<PotKind, boolean>>): void {
    let any = false;
    for (const kind of POTS) {
      this.boosterLabels[kind].visible = boosters[kind];
      any ||= boosters[kind];
    }
    this.noBoosters.visible = !any;
  }

  public show(): void {
    this.motion.preSeat();
    this.chrome.visible = true;
  }

  public hide(): void {
    this.chrome.visible = false;
    this.motion.stop();
  }

  public setContent(content: FeatureTriggerContent): void {
    this.amountLabel.text = content.amount ?? "";
  }

  public setSkipEnabled(enabled: boolean): void {
    this.chrome.skipButton.visible = enabled;
  }

  public bindSkipTap(onPress: () => void): () => void {
    return bindTap(this.chrome.skipButton, onPress);
  }

  public playEnterAnimation(): Promise<void> {
    return this.motion.enter();
  }

  /** The card shrinks out; under the way into the bonus its dim stays, and it is gone once the explosion covers the screen. */
  public async playExitAnimation(): Promise<void> {
    const entering = this.entry.playing;
    await Promise.all([this.motion.exit({ keepDim: entering }), this.entry.whenCovered()]);
  }

  public fadeInContinueText(): Promise<void> {
    return this.motion.fadeInContinueText();
  }

  public dispose(): void {
    this.chrome.dispose();
    this.destroy({ children: true });
  }
}
